import sumBy from 'lodash/sumBy';

import type { RequiredProps } from '@cast/shared-types';
import {
  NodeConfigurationTemplate,
  NodeResourceOffering,
  Node,
  EKSNodeConfigurationImageFamily,
  AKSNodeConfigurationImageFamily,
  NodeSelectionTemplateSummary,
  NodeTemplateTaint,
} from '@cast/types';

import {
  getNodeConfigurationName,
  getNodeConfigurationVersion,
} from 'utils/cluster';

import {
  nodeTemplateCriterion,
  openKarpenterSearchKey,
  openSelectionTplSearchKey,
  tableId,
} from './constants';
import { EnrichedNodeConfigTemplate, NodesSummary } from '../_types';
import { EnrichedSelectionTemplate } from './types';

import { makeSearchQuery } from '@cast/shared-search-ui';

export const openConfigTplSearchKey = 'configTemplate';

type TemplateStats = NodeSelectionTemplateSummary['stats'];

export const isTaintWithKey = (
  taint: NodeTemplateTaint
): taint is RequiredProps<NodeTemplateTaint, 'key'> => {
  return !!taint.key;
};

export const getTemplateNodesSummary = (
  stats: TemplateStats | undefined
): NodesSummary => {
  return {
    nodesCount: stats ? sumBy(Object.values(stats), (n) => n) : 0,
    [NodeResourceOffering.ON_DEMAND]: stats?.countOnDemand || 0,
    [NodeResourceOffering.SPOT]: stats?.countSpot || 0,
    [NodeResourceOffering.FALLBACK]: stats?.countFallback || 0,
    'sole-tenant': 0,
  };
};

type AutoscalerLinkArgs = {
  clusterId: string;
};

export const makeAutoscalerLink = ({ clusterId }: AutoscalerLinkArgs): string =>
  `/external-clusters/${clusterId}/autoscaler/settings`;

type NodeConfigTemplatesLinkArgs = {
  clusterId: string;
  search?: {
    openConfig?: string;
  };
};

export const makeNodeConfigTemplatesLink = ({
  clusterId,
  search,
}: NodeConfigTemplatesLinkArgs): string => {
  const link = `/automation/external-clusters/${clusterId}/autoscaler/settings/node-configurations`;

  if (search?.openConfig) {
    return `${link}?${openConfigTplSearchKey}=${search.openConfig}`;
  }
  return link;
};

type NodeSelectionTemplateSearch = {
  filterByName?: string[];
};

type SelectionTemplatesLinkArgs = {
  clusterId: string;
  search?: NodeSelectionTemplateSearch;
  openTemplate?: string;
  openKarpenter?: boolean;
};

export const makeSelectionTemplatesLink = ({
  clusterId,
  search,
  openTemplate,
  openKarpenter,
}: SelectionTemplatesLinkArgs): string => {
  const link = `/automation/external-clusters/${clusterId}/autoscaler/settings/node-templates`;
  const searchParams = new URLSearchParams();
  if (openKarpenter) {
    searchParams.append(openKarpenterSearchKey, 'true');
  }
  if (openTemplate) {
    searchParams.append(openSelectionTplSearchKey, openTemplate);
  }
  if (search?.filterByName?.length) {
    makeSearchQuery({
      id: tableId,
      values: {
        [nodeTemplateCriterion.key]: search.filterByName,
      },
      searchParams,
    });
  }
  const searchString = searchParams.toString();
  if (!searchString) {
    return link;
  }
  return `${link}?${searchString}`;
};

export const enrichNodeConfigurationTemplate = (
  nodeConfigTemplate: NodeConfigurationTemplate,
  nodes: Node[],
  selectionTemplates: EnrichedSelectionTemplate[]
): EnrichedNodeConfigTemplate => {
  const name = nodeConfigTemplate.name;
  const tplVersion = nodeConfigTemplate.version || 0;
  let nodesRunningOnOlderVersion = 0;
  let nodesCount = 0;
  const nodesByResourceOffering = {
    [NodeResourceOffering.ON_DEMAND]: 0,
    [NodeResourceOffering.SPOT]: 0,
    [NodeResourceOffering.FALLBACK]: 0,
    'sole-tenant': 0,
  };
  const linkedNodeSelectionTemplates = selectionTemplates?.filter(
    (template) => template?.configurationId === nodeConfigTemplate.id
  );

  for (const node of nodes) {
    const tplName = getNodeConfigurationName(node);
    const version = getNodeConfigurationVersion(node);
    if (tplName === name) {
      if (version) {
        nodesCount++;
        if (parseInt(version) < tplVersion) {
          nodesRunningOnOlderVersion++;
        }
        nodesByResourceOffering[node.resourceOffering]++;
      } else {
        console.error('Node with template but with no version info is found');
      }
    }
  }

  return {
    ...nodeConfigTemplate,
    linkedNodeSelectionTemplates,
    nodes: {
      nodesRunningOnOlderVersion,
      nodesCount,
      ...nodesByResourceOffering,
    },
    orig: nodeConfigTemplate,
  };
};

export const isSupportedLiveMigrationImageFamily = (
  imageFamily: EKSNodeConfigurationImageFamily | AKSNodeConfigurationImageFamily
): boolean => {
  switch (imageFamily) {
    case EKSNodeConfigurationImageFamily.FAMILY_AL2023:
    case EKSNodeConfigurationImageFamily.family_al2023:
      return true;
    default:
      return false;
  }
};
